import { z } from "zod";

export { canonicalTimeZone, TIME_ZONE_ALIASES, TIME_ZONES } from "./timezones.ts";

/** CLI binaries in the sandbox image; ones a group doesn't allow are deleted at container start. */
export const CLI_TOOLS = ["git", "gh", "node", "python", "claude"] as const;
export type CliTool = (typeof CLI_TOOLS)[number];

/** Drops tool names this version no longer ships (e.g. stored before the list changed). */
const cliToolList = z.preprocess(
  (v) => (Array.isArray(v) ? v.filter((x) => (CLI_TOOLS as readonly unknown[]).includes(x)) : v),
  z.array(z.enum(CLI_TOOLS)),
);

/**
 * What a group's members may give their agent. Every flag is enforced by Krut
 * when it renders the user's Hermes config or pod spec — never by trusting the agent.
 * Network-level internet blocking was deliberately dropped (no policy engine on the cluster).
 */
export const permissionsSchema = z.object({
  terminal: z.boolean().default(true),
  codeExecution: z.boolean().default(true),
  cliTools: cliToolList.default(["git", "node", "python"]),
  browser: z.boolean().default(false),
  webTools: z.boolean().default(true),
  /** Spawning subagents (delegate_task): each one is another model conversation, so it multiplies cost. */
  subagents: z.boolean().default(true),
  /** Image and video generation tools (paid media APIs). */
  mediaGeneration: z.boolean().default(false),
  fileUpload: z.boolean().default(true),
  cronJobs: z.boolean().default(true),
  /** Composio toolkit slugs, or ["*"] for all. */
  composioToolkits: z.array(z.string()).default([]),
  /** Which chat apps members may reach their assistant from (Krut's router bots). */
  messaging: z
    .object({ slack: z.boolean().default(false), discord: z.boolean().default(false), whatsapp: z.boolean().default(false) })
    .default({ slack: false, discord: false, whatsapp: false }),
  publishSkills: z.boolean().default(false),
  promoteMemory: z.boolean().default(true),
  /** Their assistant may send requests to colleagues' assistants and receive theirs. */
  colleagueRequests: z.boolean().default(true),
  /** Adding their own private MCP servers (company-wide ones are available to everyone regardless). */
  customMcp: z.boolean().default(true),
  voice: z.boolean().default(false),
});
export type Permissions = z.infer<typeof permissionsSchema>;

export const quotasSchema = z.object({
  /** Hard monthly LLM spend cap in USD; null = unlimited. */
  monthlyBudgetUsd: z.number().nonnegative().nullable().default(20),
  storageGb: z.number().int().min(1).max(1024).default(10),
  memoryMb: z.number().int().min(256).max(16384).default(1024),
  /** Memory for the shell sandbox — user code that OOMs dies here, not the agent. */
  sandboxMemoryMb: z.number().int().min(256).max(16384).default(1024),
});
export type Quotas = z.infer<typeof quotasSchema>;

export const groupInputSchema = z.object({
  name: z.string().trim().min(1).max(64),
  description: z.string().max(500).default(""),
  quotas: quotasSchema,
  permissions: permissionsSchema,
  isDefault: z.boolean().default(false),
});
export type GroupInput = z.infer<typeof groupInputSchema>;

export const SSO_PROVIDERS = ["google", "microsoft"] as const;
export type SsoProviderId = (typeof SSO_PROVIDERS)[number];

export const ssoProviderInputSchema = z.object({
  enabled: z.boolean(),
  clientId: z.string().trim().min(1),
  /** Omit to keep the stored secret. */
  clientSecret: z.string().trim().min(1).optional(),
  /** Microsoft only: tenant id, or "common" / "organizations". */
  tenantId: z.string().trim().optional(),
});
export type SsoProviderInput = z.infer<typeof ssoProviderInputSchema>;

export const signupPolicySchema = z.object({
  /** "invite" = only users an admin created may sign in; "domain" = anyone from allowedDomains is auto-created. */
  mode: z.enum(["invite", "domain"]).default("invite"),
  allowedDomains: z.array(z.string().trim().toLowerCase().min(1)).default([]),
});
export type SignupPolicy = z.infer<typeof signupPolicySchema>;

export const ROLES = ["admin", "user"] as const;
export type Role = (typeof ROLES)[number];
export const USER_STATUSES = ["active", "disabled"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const userInputSchema = z.object({
  email: z.email().trim().toLowerCase(),
  name: z.string().trim().max(120).default(""),
  role: z.enum(ROLES).default("user"),
  groupId: z.string().nullable().default(null),
  status: z.enum(USER_STATUSES).default("active"),
});
export type UserInput = z.infer<typeof userInputSchema>;
/**
 * Only the fields sent. Not `userInputSchema.partial()`: Zod 4 still applies the defaults of optional
 * fields, so changing the role would also reset the group to none, blank the name, and so on.
 */
export const userPatchSchema = z.object({
  name: z.string().trim().max(120).optional(),
  role: z.enum(ROLES).optional(),
  groupId: z.string().nullable().optional(),
  status: z.enum(USER_STATUSES).optional(),
});

/* ── LLM providers & model allowlist ───────────────────────────────────── */

export const LLM_PROVIDER_KINDS = ["openrouter", "openai"] as const;
/**
 * How OpenRouter picks among the many hosts of a model. "price" (OpenRouter's default) often lands on
 * slow hosts and hops between them, losing prompt-cache hits; "throughput" measured ~3–4× faster.
 */
export const LLM_ROUTINGS = ["throughput", "latency", "price"] as const;
export type LlmRouting = (typeof LLM_ROUTINGS)[number];
export type LlmProviderKindT = (typeof LLM_PROVIDER_KINDS)[number];

export const llmProviderInputSchema = z.object({
  name: z.string().trim().min(1).max(60),
  kind: z.enum(LLM_PROVIDER_KINDS),
  baseUrl: z.url().trim(),
  /** Omit to keep the stored key. */
  apiKey: z.string().trim().min(1).optional(),
  enabled: z.boolean().default(true),
  /** OpenRouter only. */
  routing: z.enum(LLM_ROUTINGS).default("throughput"),
});
export type LlmProviderInput = z.infer<typeof llmProviderInputSchema>;

export const llmModelInputSchema = z.object({
  id: z.string().trim().min(1).max(200),
  providerId: z.string().trim().min(1),
  label: z.string().trim().max(120).default(""),
  inputPerM: z.number().nonnegative().default(0),
  outputPerM: z.number().nonnegative().default(0),
  contextLength: z.number().int().positive().nullable().default(null),
});
export type LlmModelInput = z.infer<typeof llmModelInputSchema>;

/** Ordered: the first model is the default, the rest are fallbacks in order. */
export const llmModelsInputSchema = z
  .array(llmModelInputSchema)
  .min(1, "Keep at least one model")
  .max(30)
  .refine((ms) => new Set(ms.map((m) => m.id)).size === ms.length, "Each model can appear only once");

/* ── Per-user secrets ──────────────────────────────────────────────────── */

/** Names the sandbox/runtime depends on; a user secret must never shadow them. */
const RESERVED_EXACT = new Set(["PATH", "HOME", "USER", "LOGNAME", "SHELL", "PWD", "TERM", "HOSTNAME", "NPM_CONFIG_PREFIX", "TZ"]);
const RESERVED_PREFIXES = ["LD_", "SSH_", "KRUT_", "HERMES_", "API_SERVER_", "HINDSIGHT_", "TERMINAL_", "NOUS_"];

/**
 * LLM provider credentials Hermes (the pinned build) picks up from its environment. Given to the
 * assistant itself they could route AI calls around Krut's gateway — and its budgets. They are
 * still allowed in the sandbox, where e.g. the user's own Claude Code uses them on their own bill.
 */
export const ASSISTANT_DENIED_SECRETS = new Set([
  "ACTUAL_API_KEY", "AI_GATEWAY_API_KEY", "ALIBABA_CODING_PLAN_API_KEY", "ALIBABA_CODING_PLAN_CN_API_KEY", "ALIBABA_TOKEN_PLAN_API_KEY",
  "ALIBABA_TOKEN_PLAN_CN_API_KEY", "ANTHROPIC_API_KEY", "ANTHROPIC_TOKEN", "ARCEEAI_API_KEY", "AWS_ACCESS_KEY_ID", "AWS_SECRET_ACCESS_KEY",
  "AWS_SESSION_TOKEN", "AZURE_FOUNDRY_API_KEY", "CLAUDE_CODE_OAUTH_TOKEN", "COMMANDCODE_API_KEY", "COPILOT_GITHUB_TOKEN", "DASHSCOPE_API_KEY",
  "DEEPINFRA_API_KEY", "DEEPSEEK_API_KEY", "FIREWORKS_API_KEY", "GEMINI_API_KEY", "GH_TOKEN", "GITHUB_TOKEN", "GLM_API_KEY", "GMI_API_KEY",
  "GOOGLE_API_KEY", "GOOGLE_APPLICATION_CREDENTIALS", "HF_TOKEN", "KILOCODE_API_KEY", "KIMI_API_KEY", "KIMI_CN_API_KEY", "KIMI_CODING_API_KEY",
  "LM_API_KEY", "META_API_KEY", "META_MODEL_API_KEY", "MINIMAX_API_KEY", "MINIMAX_CN_API_KEY", "MODEL_API_KEY", "NEBIUS_API_KEY",
  "NEBIUS_TOKEN_FACTORY_API_KEY", "NOVITA_API_KEY", "NVIDIA_API_KEY", "OLLAMA_API_KEY", "OPENAI_API_KEY", "OPENCODE_GO_API_KEY",
  "OPENCODE_ZEN_API_KEY", "OPENROUTER_API_KEY", "QWEN_API_KEY", "RAMP_ROUTER_API_KEY", "ROUTER_API_KEY", "STEPFUN_API_KEY", "TOKENHUB_API_KEY",
  "TOKENPLAN_API_KEY", "UPSTAGE_API_KEY", "XAI_API_KEY", "XIAOMI_API_KEY", "ZAI_API_KEY", "Z_AI_API_KEY", "GROQ_API_KEY", "MISTRAL_API_KEY",
  "COHERE_API_KEY", "TOGETHER_API_KEY", "PERPLEXITY_API_KEY", "CEREBRAS_API_KEY",
]);

export function secretNameProblem(name: string, exposeToAssistant: boolean): string | null {
  if (!/^[A-Z][A-Z0-9_]{1,63}$/.test(name)) return "Use UPPER_SNAKE_CASE: letters, digits and underscores, starting with a letter.";
  if (RESERVED_EXACT.has(name) || RESERVED_PREFIXES.some((p) => name.startsWith(p))) return `${name} is reserved by Krut AI.`;
  if (exposeToAssistant && (ASSISTANT_DENIED_SECRETS.has(name) || /_(BASE_URL|API_BASE)$/.test(name))) {
    return `${name} can be used by your sandbox (commands, CLIs) but not by the assistant itself — it could bypass your organisation's AI gateway.`;
  }
  return null;
}

export const userSecretInputSchema = z
  .object({
    name: z.string().trim().toUpperCase(),
    /** Omit on update to keep the stored value. */
    value: z.string().min(1).max(8192).refine((v) => !/[\r\n]/.test(v), "Single-line values only").optional(),
    description: z.string().trim().max(200).default(""),
    exposeToAssistant: z.boolean().default(false),
  })
  .superRefine((s, ctx) => {
    const problem = secretNameProblem(s.name, s.exposeToAssistant);
    if (problem) ctx.addIssue({ code: "custom", path: ["name"], message: problem });
  });
export type UserSecretInput = z.infer<typeof userSecretInputSchema>;

/* ── Messaging connectors ──────────────────────────────────────────────── */

export const MESSAGING_PLATFORMS = ["slack", "discord", "whatsapp"] as const;
export type MessagingPlatformT = (typeof MESSAGING_PLATFORMS)[number];

export const messagingConnectorInputSchema = z.object({
  enabled: z.boolean(),
  /** Also answer @mentions in shared channels (replies are visible to the channel). DMs always work. */
  respondInChannels: z.boolean().default(false),
  /** Omit to keep stored tokens. Slack: botToken (xoxb-) + appToken (xapp-). Discord: botToken. WhatsApp: none. */
  secrets: z.record(z.string(), z.string().trim().min(1)).optional(),
});
export type MessagingConnectorInput = z.infer<typeof messagingConnectorInputSchema>;

/* ── Memory (Hindsight) settings ───────────────────────────────────────── */

export const DEFAULT_PROMOTION_RULES = `Promote a memory to company memory ONLY if it is durable, work-related knowledge that would help colleagues, for example:
- facts about the company, its products, services, pricing, processes, tools and systems;
- facts about clients, partners and projects (names, needs, decisions, status, commitments);
- decisions made and the reasons for them; lessons learned; how-to knowledge.

NEVER promote:
- anything personal: health, family, feelings, private opinions, personal plans, location, contact details;
- credentials, secrets, API keys, passwords, financial account details;
- HR matters: salaries, performance, hiring or firing of named people;
- anything the user said was private or confidential;
- facts that are only about the user's own preferences or their assistant's behaviour;
- temporary or trivial facts (what they had for lunch, a file being empty).

When you promote, rewrite the fact so it stands on its own, attributes nothing to the individual user unless their role matters, and drops any personal detail.`;

export const memorySettingsSchema = z.object({
  /** Assistants also recall from the company bank. */
  companyMemoryEnabled: z.boolean().default(false),
  /** Automatically promote eligible user memories using the rules below. */
  promotionEnabled: z.boolean().default(false),
  /** Only memories formed after this moment are considered — never retroactive. */
  promotionEnabledAt: z.string().nullable().default(null),
  promotionRules: z.string().max(8000).default(DEFAULT_PROMOTION_RULES),
  /** Model Hindsight (memory extraction) and the promoter use; null = the default model. */
  memoryModel: z.string().nullable().default(null),
});
export type MemorySettings = z.infer<typeof memorySettingsSchema>;

export const memorySettingsInputSchema = memorySettingsSchema.omit({ promotionEnabledAt: true });

/* ── Skills ────────────────────────────────────────────────────────────── */

export const DEFAULT_SKILL_RULES = `A skill may be shared with the whole company ONLY if it contains no sensitive data. Treat as sensitive:
- credentials of any kind: passwords, API keys, tokens, app passwords, private keys, connection strings;
- personal data about identifiable people: phone numbers, home addresses, personal email addresses, health, family or HR details;
- confidential client or financial data: contract values, bank or card details, customer lists with contact details;
- anything marked private or confidential.

NOT sensitive: general how-to steps, tool names, workflows, public company information, work email addresses used as an example sender, placeholders like <API_KEY>.`;

export const skillSettingsSchema = z.object({
  /** Share users' own skills (those in groups allowed to publish) with everyone once they pass the scan. */
  sharingEnabled: z.boolean().default(false),
  /** Clean skills wait for an admin instead of being shared straight away. */
  requireApproval: z.boolean().default(false),
  sensitivityRules: z.string().max(8000).default(DEFAULT_SKILL_RULES),
});
export type SkillSettings = z.infer<typeof skillSettingsSchema>;

/** Hermes skill names: also the folder name, so kept filesystem- and prompt-safe. */
export const skillNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9._-]*$/, "Use lowercase letters, digits, dots, dashes and underscores.");

export const skillFilePathSchema = z
  .string()
  .min(1)
  .max(200)
  .refine((p) => !p.startsWith("/") && !p.split("/").some((s) => s === "" || s === "." || s === ".."), "Bad file path.");

export const officialSkillInputSchema = z.object({
  name: skillNameSchema,
  /** SKILL.md plus any support files, as text. SKILL.md is required. */
  files: z
    .record(skillFilePathSchema, z.string().max(512 * 1024))
    .refine((f) => typeof f["SKILL.md"] === "string" && f["SKILL.md"].trim().length > 0, "SKILL.md is required."),
});
export type OfficialSkillInput = z.infer<typeof officialSkillInputSchema>;

/** "unshared" = listed for its owner and admins only, because sharing is off or the owner's group may not publish. */
export const SKILL_STATUSES = ["official", "shared", "pending", "blocked", "private", "hidden", "superseded", "unshared"] as const;
export type SkillStatus = (typeof SKILL_STATUSES)[number];

/** The admin Skills list is split into these tabs; each shows skills in any of its statuses. */
export const SKILL_TABS = [
  { id: "official", label: "Official", statuses: ["official"] },
  { id: "shared", label: "Shared", statuses: ["shared"] },
  { id: "pending", label: "Needs review", statuses: ["pending"] },
  { id: "blocked", label: "Blocked", statuses: ["blocked"] },
  { id: "other", label: "Not shared", statuses: ["private", "hidden", "unshared"] },
] as const satisfies readonly { id: string; label: string; statuses: readonly SkillStatus[] }[];
export type SkillTabId = (typeof SKILL_TABS)[number]["id"];

/* ── Knowledge sync ────────────────────────────────────────────────────── */

export const SYNC_SCHEDULES = ["manual", "hourly", "daily", "weekly"] as const;
export type SyncSchedule = (typeof SYNC_SCHEDULES)[number];
export const SYNC_JOB_STATUSES = ["active", "paused", "proposed", "rejected"] as const;
export type SyncJobStatus = (typeof SYNC_JOB_STATUSES)[number];

const toolkitSlug = z.string().regex(/^[a-z0-9_-]{1,60}$/);

export const syncSourceSchema = z.discriminatedUnion("kind", [
  /** One or more apps connected under Krut's company Composio identity. */
  z.object({ kind: z.literal("composio"), toolkits: z.array(toolkitSlug).min(1).max(5) }),
  /** Any MCP server reachable from Krut. Header values are secrets (stored encrypted, never returned). */
  z.object({
    kind: z.literal("mcp"),
    url: z.string().url().max(500).refine((u) => /^https?:\/\//.test(u), "Use an http(s) URL."),
    headers: z.record(z.string().regex(/^[A-Za-z0-9-]{1,60}$/), z.string().max(4000)).optional(),
  }),
]);
export type SyncSource = z.infer<typeof syncSourceSchema>;

export const syncJobInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  source: syncSourceSchema,
  /** Plain English: what to sync and what to leave out. */
  instructions: z.string().trim().min(10).max(4000),
  schedule: z.enum(SYNC_SCHEDULES).default("daily"),
  /** Empty = everyone; otherwise only members of these groups recall what this job syncs. */
  groupIds: z.array(z.string()).max(50).default([]),
  /** Null = the default model. Needs a model good at tool use. */
  model: z.string().nullable().default(null),
  maxDocumentsPerRun: z.number().int().min(1).max(2000).default(200),
  maxCostPerRunUsd: z.number().min(0.01).max(100).default(1),
  enabled: z.boolean().default(true),
});
export type SyncJobInput = z.infer<typeof syncJobInputSchema>;

/* ── Colleague requests & approvals ─────────────────────────────────────── */

export const APPROVAL_STATUSES = ["pending", "approved", "rejected", "cancelled"] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];
/** Where a person decided an approval. */
export const APPROVAL_CHANNELS = ["web", "slack", "discord", "whatsapp"] as const;
export type ApprovalChannel = (typeof APPROVAL_CHANNELS)[number];
export const REQUEST_STATUSES = ["open", "awaiting_approval", "answered", "declined"] as const;
export type ColleagueRequestStatus = (typeof REQUEST_STATUSES)[number];

export const approvalDecisionSchema = z.object({
  decision: z.enum(["approve", "reject"]),
  note: z.string().max(2000).optional(),
});

/* ── MCP servers ─────────────────────────────────────────────────────────── */

export const MCP_AUTH_KINDS = ["none", "headers", "oauth"] as const;
export type McpAuthKind = (typeof MCP_AUTH_KINDS)[number];
/** Company servers: one set of credentials for everyone ("shared"), or each person connects their own. */
export const MCP_CREDENTIAL_MODES = ["shared", "personal"] as const;
export type McpCredentialMode = (typeof MCP_CREDENTIAL_MODES)[number];
export const MCP_CONNECTION_STATUSES = ["pending", "connected", "expired", "error"] as const;
export type McpConnectionStatus = (typeof MCP_CONNECTION_STATUSES)[number];

const mcpHeaderName = z.string().regex(/^[A-Za-z0-9-]{1,60}$/, "Header names use letters, digits and dashes.");
export const mcpHeadersSchema = z.record(mcpHeaderName, z.string().min(1).max(4000));

export const mcpServerInputSchema = z.object({
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(300).default(""),
  url: z.string().trim().url().max(500).refine((u) => /^https?:\/\//.test(u), "Use an http(s) URL."),
  auth: z.enum(MCP_AUTH_KINDS).default("none"),
  /** Company servers only; a person's own server always uses their own credentials. */
  credentials: z.enum(MCP_CREDENTIAL_MODES).default("shared"),
  /** Header auth: the headers to fill in (e.g. Authorization). Values are given when connecting. */
  headerNames: z.array(mcpHeaderName).max(5).default([]),
  /** OAuth: a pre-registered client, for servers without dynamic client registration (e.g. GitHub). */
  oauthClientId: z.string().trim().max(300).default(""),
  /** Write-only; omitted = keep the stored one. */
  oauthClientSecret: z.string().max(1000).optional(),
  oauthScope: z.string().trim().max(500).default(""),
  enabled: z.boolean().default(true),
});
export type McpServerInput = z.infer<typeof mcpServerInputSchema>;

export const mcpConnectInputSchema = z.object({ headers: mcpHeadersSchema.optional() });

/* ── Branding (white-label) ──────────────────────────────────────────────── */

export const DEFAULT_BRAND_NAME = "Krut AI";
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a colour like #1f7fbf.");
/** PNG, JPEG, WebP, SVG or ICO as a data URL, up to ~500 KB. */
const brandImage = z
  .string()
  .max(700_000, "Images can be up to 500 KB.")
  .regex(/^data:image\/(png|jpeg|webp|svg\+xml|x-icon|vnd\.microsoft\.icon);base64,[A-Za-z0-9+/]+=*$/, "Use a PNG, JPEG, WebP, SVG or ICO image.");

/** WCAG relative luminance of #rrggbb, 0 (black) … 1 (white). */
export function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export const brandingInputSchema = z.object({
  name: z.string().trim().min(1).max(40).default(DEFAULT_BRAND_NAME),
  /** Shown on the dark sidebar and sign-in header: a light or white logo works best. */
  logo: brandImage.nullable().default(null),
  favicon: brandImage.nullable().default(null),
  /** Buttons, links, switches and focus rings. */
  accentColor: hexColor.default("#1f7fbf"),
  /** Sidebar and primary buttons; white text sits on it, so it must be dark. */
  sidebarColor: hexColor.refine((c) => luminance(c) <= 0.2, "Pick a darker sidebar colour: white text has to stay readable on it.").default("#333d4d"),
});
export type BrandingInput = z.infer<typeof brandingInputSchema>;
export const brandingSchema = brandingInputSchema.extend({ revision: z.number().int().default(0) });
export type Branding = z.infer<typeof brandingSchema>;
